import { useQuery } from "@tanstack/react-query";
import api from "../../http";
import { useForBindingId } from "../../../forBinding";
import type { ParcelamentoAPI, ParcelamentoResponse, ParcellingType } from "@practium/features/parcelling";

type ParcellingTypeFilter = ParcellingType | "all";

const matchesParcellingType = (doc: ParcelamentoAPI, typeParcelling: ParcellingTypeFilter) => {
	if (typeParcelling === "all") return true;
	return (doc.typeParcelling || "fixed") === typeParcelling;
};

interface UseGetParcellingAccessibleParams {
	page?: number;
	limit?: number;
	enabled?: boolean;
	typeParcelling?: ParcellingTypeFilter;
}

export const useGetParcellingAccessible = ({
	page = 1,
	limit = 200,
	enabled = true,
	typeParcelling = "fixed",
}: UseGetParcellingAccessibleParams = {}) => {
	const forBinding = useForBindingId();

	const query = useQuery({
		queryKey: ["parcelling", "accessible", forBinding, page, limit],
		enabled,
		placeholderData: (previousData, previousQuery) =>
			previousQuery?.queryKey[2] === forBinding ? previousData : undefined,
		queryFn: async () => {
			const { data } = await api.get<ParcelamentoResponse>("/advices/parcelling/findAllAccessible", {
				params: { forBinding, page, limit },
			});
			return data;
		},
	});

	return {
		...query,
		data: (query.data?.docs ?? []).filter((doc) => matchesParcellingType(doc, typeParcelling)),
		pagination: query.data
			? {
					totalDocs: query.data.totalDocs,
					totalPages: query.data.totalPages,
					page: query.data.page,
					hasPrevPage: query.data.hasPrevPage,
					hasNextPage: query.data.hasNextPage,
					prevPage: query.data.prevPage,
					nextPage: query.data.nextPage,
				}
			: null,
	};
};

